import React, { useEffect, useRef, useState } from 'react';
import { Edit2, Check, X } from 'lucide-react';

const TripTimeline = ({ trip, activePhotoId, onPhotoInView, onPhotoUpdate }) => {
    const activePhoto = trip?.photos.find(p => p.id === activePhotoId);
    const filmstripRef = useRef(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editCaption, setEditCaption] = useState('');

    // Scroll filmstrip to active photo
    useEffect(() => {
        if (activePhotoId && filmstripRef.current) {
            const element = document.getElementById(`photo-${activePhotoId}`);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
            }
        }
        // Reset edit mode when photo changes
        setIsEditing(false);
    }, [activePhotoId]);

    // Initialize edit caption when entering edit mode
    useEffect(() => {
        if (isEditing && activePhoto) {
            setEditCaption(activePhoto.caption || trip.city || '');
        }
    }, [isEditing, activePhoto, trip.city]);

    const handleSaveCaption = () => {
        if (activePhoto) {
            onPhotoUpdate(activePhoto.id, { caption: editCaption });
            setIsEditing(false);
        }
    };

    if (!trip) return <div className="trip-timeline">Select a trip</div>;

    return (
        <>
            <div className="active-photo-view">
                {activePhoto ? (
                    <>
                        <img
                            src={activePhoto.src}
                            alt={activePhoto.caption || "Trip memory"}
                            className="main-photo"
                        />
                        <div className="photo-info">
                            <div className="photo-date">
                                {new Date(activePhoto.timestamp).toLocaleDateString()} {new Date(activePhoto.timestamp).toLocaleTimeString()}
                            </div>

                            <div className="caption-container">
                                {isEditing ? (
                                    <div className="caption-edit-mode">
                                        <input
                                            autoFocus
                                            type="text"
                                            value={editCaption}
                                            onChange={(e) => setEditCaption(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') handleSaveCaption();
                                                if (e.key === 'Escape') setIsEditing(false);
                                            }}
                                            className="caption-input"
                                            placeholder="Add a caption..."
                                        />
                                        <div className="caption-actions">
                                            <button onClick={handleSaveCaption} className="caption-btn save">
                                                <Check size={16} />
                                            </button>
                                            <button onClick={() => setIsEditing(false)} className="caption-btn cancel">
                                                <X size={16} />
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="caption-display-mode">
                                        <p className="photo-caption">
                                            {activePhoto.caption || trip.city || <span className="placeholder-caption">Add a caption...</span>}
                                        </p>
                                        <button onClick={() => setIsEditing(true)} className="caption-btn edit">
                                            <Edit2 size={14} />
                                        </button>
                                    </div>
                                )}
                            </div>

                            {(!activePhoto.lat || !activePhoto.lng) && <span className="no-location-badge">No Location Data</span>}
                        </div>
                    </>
                ) : (
                    <div className="no-photo-selected">Select a photo to view</div>
                )}
            </div>

            <div className="filmstrip" ref={filmstripRef}>
                {trip.photos.map(photo => (
                    <img
                        key={photo.id}
                        id={`photo-${photo.id}`}
                        src={photo.src}
                        alt="Thumbnail"
                        className={`filmstrip-thumb ${activePhotoId === photo.id ? 'active' : ''}`}
                        onClick={() => onPhotoInView(photo.id)}
                    />
                ))}
            </div>
        </>
    );
};

export default TripTimeline;
